import { createSlice } from "@reduxjs/toolkit";

const warehouseSlice = createSlice({
  name: "warehouse",
  initialState: {
    // Current warehouse being edited
    currentWarehouse: null,

    // All warehouses list
    warehouses: [],

    // Elements on the canvas (racks, zones, features)
    elements: [],

    // Selected elements for editing
    selectedElements: [],

    // Clipboard for copy/paste
    clipboard: [],

    // Undo/Redo history
    history: {
      past: [],
      present: null,
      future: [],
    },

    // Canvas view settings
    zoom: 1,
    pan: { x: 0, y: 0 },
    gridSize: 20, // pixels per grid unit
    snapToGrid: true,
    showGrid: true,

    // Active layer for filtering elements
    activeLayer: "all", // 'all', 'racks', 'zones', 'features'

    // Loading and error states
    loading: false,
    error: null,

    // Save status
    lastSaved: null,
    hasUnsavedChanges: false,
  },
  reducers: {
    // Warehouse management
    setWarehouses: (state, action) => {
      state.warehouses = action.payload;
    },
    setCurrentWarehouse: (state, action) => {
      state.currentWarehouse = action.payload;
      state.hasUnsavedChanges = false;
    },

    // Element management
    setElements: (state, action) => {
      state.elements = action.payload;
      state.hasUnsavedChanges = false;
    },
    addElement: (state, action) => {
      state.elements.push(action.payload);
      state.hasUnsavedChanges = true;
      // Add to history
      state.history.past.push(JSON.stringify(state.elements));
      state.history.future = [];
    },
    updateElement: (state, action) => {
      const index = state.elements.findIndex((e) => e.id === action.payload.id);
      if (index !== -1) {
        state.elements[index] = { ...state.elements[index], ...action.payload };
        state.hasUnsavedChanges = true;
      }
    },
    deleteElement: (state, action) => {
      state.elements = state.elements.filter((e) => e.id !== action.payload);
      state.selectedElements = state.selectedElements.filter((id) => id !== action.payload);
      state.hasUnsavedChanges = true;
      // Add to history
      state.history.past.push(JSON.stringify(state.elements));
      state.history.future = [];
    },
    deleteSelectedElements: (state) => {
      state.elements = state.elements.filter(
        (e) => !state.selectedElements.includes(e.id)
      );
      state.selectedElements = [];
      state.hasUnsavedChanges = true;
    },

    // Selection management
    setSelectedElements: (state, action) => {
      state.selectedElements = action.payload;
    },
    selectElement: (state, action) => {
      if (!state.selectedElements.includes(action.payload)) {
        state.selectedElements.push(action.payload);
      }
    },
    deselectElement: (state, action) => {
      state.selectedElements = state.selectedElements.filter(
        (id) => id !== action.payload
      );
    },
    clearSelection: (state) => {
      state.selectedElements = [];
    },

    // Clipboard operations
    copyToClipboard: (state) => {
      const selectedElements = state.elements.filter((e) =>
        state.selectedElements.includes(e.id)
      );
      state.clipboard = selectedElements;
    },
    pasteFromClipboard: (state) => {
      const newElements = state.clipboard.map((element) => ({
        ...element,
        id: `${element.type}-${Date.now()}-${Math.random()}`,
        gridPosition: {
          ...element.gridPosition,
          x: element.gridPosition.x + 2,
          y: element.gridPosition.y + 2,
        },
      }));
      state.elements.push(...newElements);
      state.selectedElements = newElements.map((e) => e.id);
      state.hasUnsavedChanges = true;
    },

    // Undo/Redo
    undo: (state) => {
      if (state.history.past.length > 0) {
        const previous = state.history.past.pop();
        state.history.future.unshift(JSON.stringify(state.elements));
        state.elements = JSON.parse(previous);
        state.hasUnsavedChanges = true;
      }
    },
    redo: (state) => {
      if (state.history.future.length > 0) {
        const next = state.history.future.shift();
        state.history.past.push(JSON.stringify(state.elements));
        state.elements = JSON.parse(next);
        state.hasUnsavedChanges = true;
      }
    },
    saveToHistory: (state) => {
      state.history.past.push(JSON.stringify(state.elements));
      state.history.future = [];
    },

    // Canvas view controls
    setZoom: (state, action) => {
      state.zoom = Math.max(0.1, Math.min(5, action.payload));
    },
    setPan: (state, action) => {
      state.pan = action.payload;
    },
    setGridSize: (state, action) => {
      state.gridSize = action.payload;
    },
    toggleSnapToGrid: (state) => {
      state.snapToGrid = !state.snapToGrid;
    },
    toggleShowGrid: (state) => {
      state.showGrid = !state.showGrid;
    },
    setActiveLayer: (state, action) => {
      state.activeLayer = action.payload;
    },

    // Loading and error states
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },

    // Save status
    markAsSaved: (state) => {
      state.lastSaved = new Date().toISOString();
      state.hasUnsavedChanges = false;
    },

    // Reset state
    resetWarehouse: (state) => {
      state.currentWarehouse = null;
      state.elements = [];
      state.selectedElements = [];
      state.clipboard = [];
      state.history = { past: [], present: null, future: [] };
      state.zoom = 1;
      state.pan = { x: 0, y: 0 };
      state.hasUnsavedChanges = false;
    },
  },
});

export const {
  setWarehouses,
  setCurrentWarehouse,
  setElements,
  addElement,
  updateElement,
  deleteElement,
  deleteSelectedElements,
  setSelectedElements,
  selectElement,
  deselectElement,
  clearSelection,
  copyToClipboard,
  pasteFromClipboard,
  undo,
  redo,
  saveToHistory,
  setZoom,
  setPan,
  setGridSize,
  toggleSnapToGrid,
  toggleShowGrid,
  setActiveLayer,
  setLoading,
  setError,
  markAsSaved,
  resetWarehouse,
} = warehouseSlice.actions;

export default warehouseSlice.reducer;
